import * as RadioGroupBase from "@radix-ui/react-radio-group";
import React, {
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
} from "react";
import { useId } from "react";
import styled from "@emotion/styled";
import Checked from "./assets/CheckedRadioButton.svg?react";
import Unchecked from "./assets/UncheckedRadioButton.svg?react";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { InfoOutlinedIcon } from "src/lib/ui-icon-v2";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

import { logger } from "src/logger";

type Orientation = "horizontal" | "vertical";

export type RadioGroupV2Props = PropsWithChildren<{
  className?: string;
  orientation?: Orientation;
  defaultValue?: any;
  label?: ReactNode;
  disabled?: boolean;
  value?: any;
  isLoading?: boolean;
  onValueChange?: (value: string | undefined) => void;
  options?: RadioItemProps[];
}> &
  Omit<
    RadioGroupBase.RadioGroupProps,
    "value" | "onValueChange" | "defaultValue"
  >;

export const RadioGroupV2 = forwardRef(
  (
    {
      isLoading = false,
      children,
      options,
      disabled,
      label,
      orientation = "horizontal",
      ...rest
    }: RadioGroupV2Props,
    ref: Ref<HTMLDivElement>,
  ) => {
    if (options && children) {
      logger.error(
        "RadioGroupV2 received options and children props simultaneously. Provide either options or children",
      );
    }
    return (
      <Skeleton isLoading={isLoading}>
        <div>
          {!!label && <GroupLabel>{label}</GroupLabel>}
          <Root
            {...rest}
            disabled={disabled}
            orientation={orientation}
            ref={ref}
          >
            {(options || []).map((option, index) => (
              <RadioV2 key={option.value} disabled={disabled} {...option} />
            ))}
            {children}
          </Root>
        </div>
      </Skeleton>
    );
  },
);

export type RadioItemProps = {
  value: any;
  label?: ReactNode;
  helperText?: ReactNode;
  children?: ReactNode;
} & Omit<RadioGroupBase.RadioGroupItemProps, "value">;

export const RadioV2 = (props: RadioItemProps) => {
  const { label, helperText, disabled, children, ...rest } = props;
  const id = useId();

  return (
    <div>
      <ItemWrapper>
        <Item id={id} disabled={disabled} {...rest}>
          <Indicator forceMount>
            <Unchecked />
            <Checked />
          </Indicator>
        </Item>
        {!!label && (
          <Label htmlFor={id} disabled={disabled}>
            {label}
          </Label>
        )}
        {helperText && (
          <HelperTextIcon>
            <TooltipV2 cursor={"help"} content={helperText}>
              <InfoOutlinedIcon />
            </TooltipV2>
          </HelperTextIcon>
        )}
      </ItemWrapper>
      {children && <ChildrenWrapper>{children}</ChildrenWrapper>}
    </div>
  );
};

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ChildrenWrapper = styled.div`
  margin-top: 12px;
  margin-bottom: 20px;
`;

const Indicator = styled(RadioGroupBase.Indicator)`
  display: flex;
  border-radius: 100%;
  transition: box-shadow 0.25s;

  svg:first-of-type {
    display: block;
  }

  svg:last-child {
    display: none;
  }

  &[data-state="checked"] {
    svg:first-of-type {
      display: none;
    }

    svg:last-child {
      display: block;
    }
  }
`;

const Item = styled(RadioGroupBase.Item)`
  outline: none;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;

  border-radius: 50%;
  &:not(:disabled):hover ${Indicator} {
    box-shadow: 0 0 0 5px ${({ theme }) => theme.outlineV2.radioButton};
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.colorsV2.grey[100]};

    & circle {
      fill: ${({ theme }) => theme.colorsV2.fg.muted};
    }

    & rect {
      stroke: ${({ theme }) => theme.colorsV2.fg.muted};
    }
  }
`;

const Label = styled.label<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-left: 6px;
`;

const HelperTextIcon = styled.div`
  margin-left: 5px;
`;

const Root = styled(RadioGroupBase.Root)<{ disabled?: boolean }>`
  display: flex;
  font-size: ${({ theme }) => theme.fontSizesV2.body};
  & ${Item} {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  & circle {
    fill: ${({ disabled, theme }) =>
      disabled ? theme.colorsV2.fg.muted : theme.colorsV2.info.bg};
  }

  & rect {
    stroke: ${({ disabled, theme }) =>
      disabled ? theme.colorsV2.fg.muted : theme.colorsV2.info.bg};
  }

  &[data-orientation="vertical"] {
    flex-direction: column;
    gap: 10px;
  }

  &[data-orientation="horizontal"] {
    flex-direction: row;
    gap: 15px;
  }
`;

const GroupLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizesV2.h6};
  color: ${({ theme }) => theme.colorsV2.grey[900]};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  height: 21px;
  display: flex;
`;
